import { Link } from "react-router-dom";
import { Brain, Activity, Heart, ArrowRight, Sparkles, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";

/**
 * Mental Performance & Wellness Intelligence™
 * ────────────────────────────────────────────
 * Full public-facing module page. This is one of the eight Intelligence
 * Domains™ and — per founder direction — the one every program says
 * they want but few operationalize. Content here mirrors what the module
 * measures, tracks, and escalates inside the coach + player portals.
 */

const PILLARS = [
  { icon: Brain,   tone: "#22D3EE", label: "Cognitive Load",
    body: "Practice heaviness, decision fatigue, dual-task cost — mapped to VisionIQ™ signal decay across the session." },
  { icon: Heart,   tone: "#A78BFA", label: "Emotional Readiness",
    body: "Daily mood + stress + confidence surveys, anonymized to medical, mapped to workload state and recovery." },
  { icon: Shield,  tone: "#F97316", label: "Resilience",
    body: "Longitudinal resilience score — how quickly the athlete's wellness recovers after high-stress days." },
  { icon: Activity,tone: "#10B981", label: "Sleep-latency signals",
    body: "Time-to-sleep, restlessness, wake-count patterns from wearables — a leading indicator of mental fatigue." },
  { icon: Sparkles,tone: "#EF4444", label: "Cognitive-Load × Injury Risk",
    body: "When mental load runs high while physical load is elevated, injury risk rises non-linearly. We escalate before it breaks." },
  { icon: Brain,   tone: "#38BDF8", label: "Return-to-Confidence",
    body: "Post-injury psychology tracking — the missing half of Return-to-Play frameworks that predicts re-injury risk." },
];

const WORKFLOWS = [
  { role: "Coach",     body: "Daily red/yellow/green wellness triage across the roster. Individual notes routed with practice load." },
  { role: "Trainer",   body: "Automatic escalation when a resilience score, mood, or sleep-latency window breaks pattern." },
  { role: "Sports Psych",body: "Anonymized aggregate trends + opt-in individual timelines. Medical-grade privacy scoping." },
  { role: "Athletic Director", body: "Program-wide wellness dashboards. Retention, culture, and mental-health outcomes at the department level." },
  { role: "Athlete",   body: "60-second daily check-in inside the AthleteIQ™ app. Streaks, coping cards, coaches' feedback loops." },
];

export default function MentalPerformance() {
  return (
    <div className="min-h-screen bg-[#050810] text-white" data-testid="mental-performance-page">
      <SiteNav />
      <section className="relative pt-32 pb-16 border-b border-white/8 overflow-hidden">
        <div className="absolute inset-0 opacity-40 pointer-events-none">
          <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-cyan-500/20 blur-[120px]" />
          <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-fuchsia-500/20 blur-[120px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-[10px] tracking-[0.4em] uppercase font-bold text-cyan-300/80 mb-3 flex items-center gap-2">
            <Brain className="h-3.5 w-3.5" /> Intelligence Domain 07 · Mental Performance & Wellness Intelligence™
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.05]">
            The wellness engine every<br/>
            <span className="text-white/50">program wants — and few operationalize.</span>
          </h1>
          <p className="mt-6 text-white/75 max-w-2xl leading-relaxed">
            MP&W Intelligence&trade; turns culture-talk into a daily workflow. Every athlete&apos;s cognitive load, emotional
            state, resilience, and sleep-latency signals feed the same intelligence layer as their physical
            performance — so every readiness decision is a whole-athlete decision.
          </p>
          <div className="mt-8">
            <Link to="/request-demo" data-testid="mp-cta-demo">
              <Button size="lg" className="text-white font-bold text-sm px-6 py-6" style={{ backgroundColor: "var(--brand-secondary)" }}>
                Request demo <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 max-w-7xl mx-auto px-6">
        <div className="text-[10px] tracking-[0.4em] uppercase font-bold text-cyan-300/80 mb-3">What it measures</div>
        <h2 className="font-display font-black text-3xl text-white tracking-tight">Six mental-performance pillars.</h2>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {PILLARS.map((p) => (
            <div key={p.label} className="ink-card rounded-2xl p-5" data-testid={`mp-pillar-${p.label.toLowerCase().replace(/[^a-z]+/g,'-')}`}>
              <p.icon className="h-5 w-5 mb-3" style={{ color: p.tone }} />
              <div className="font-display font-black text-white text-base leading-tight">{p.label}</div>
              <div className="mt-2 text-sm text-white/65 leading-relaxed">{p.body}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-16 border-t border-white/8 max-w-7xl mx-auto px-6">
        <div className="text-[10px] tracking-[0.4em] uppercase font-bold text-cyan-300/80 mb-3">Workflows</div>
        <h2 className="font-display font-black text-3xl text-white tracking-tight">Every seat in the department has a role.</h2>
        <div className="mt-8 space-y-3">
          {WORKFLOWS.map((w) => (
            <div key={w.role} className="ink-card rounded-2xl p-6 grid md:grid-cols-4 gap-4" data-testid={`mp-workflow-${w.role.toLowerCase().replace(/[^a-z]+/g,'-')}`}>
              <div className="font-display font-black text-white text-lg">{w.role}</div>
              <div className="md:col-span-3 text-sm text-white/75 leading-relaxed">{w.body}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-16 border-t border-white/8 text-center">
        <h2 className="font-display font-black text-3xl text-white tracking-tight">
          Ready to operationalize wellness?
        </h2>
        <div className="mt-6">
          <Link to="/request-demo">
            <Button size="lg" className="text-white font-bold text-sm px-6 py-6" style={{ backgroundColor: "var(--brand-secondary)" }}>
              Request demo <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}
